import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchUtilizationRisk,
  imogenSelector,
} from "../../../../../redux/slices/imogen";

const SOURCE_LABEL = {
  storage_utilization_latest: "Storage utilization",
};

const formatLastRefresh = (iso) => {
  if (!iso) return null;
  return moment.utc(iso).format("MMM D, HH:mm [UTC]");
};

const formatSourceTimestamp = (iso) => {
  if (!iso) return "unknown";
  return moment.utc(iso).format("MMM D, YYYY");
};

const formatWeeks = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(1);
};

const UNIT_LABEL = {
  cubic_foot: "ft³",
  cubic_metre: "m³",
};

const formatCurrency = (value, currency) => {
  if (value == null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch (e) {
    return `${Number(value).toFixed(2)} ${currency || ""}`.trim();
  }
};

// Least-to-most-severe. Tiles are colored by where the tile's own
// surcharge_tiers places the current ratio, not a fixed week count — tier
// boundaries and counts differ per marketplace/storage_type (e.g. US
// Standard has 5 paid tiers on a 22-week ladder, CA Oversize has 2 on a
// 26-week ladder).
const SEVERITY_PALETTE = [
  "#2e7d32",
  "#f9a825",
  "#ef6c00",
  "#e64a19",
  "#c62828",
  "#7f0000",
];

const findTierIndex = (tiers, weeks) => {
  if (!tiers || !tiers.length || weeks == null) return -1;
  return tiers.findIndex(
    (t) => t.min_weeks <= weeks && (t.max_weeks == null || weeks < t.max_weeks)
  );
};

// Tiles scraped before surcharge_tiers existed (or a scrape gap) have no
// tier data yet — fall back to the old US ladder rather than showing an
// uncolored tile.
const getWeeksColorLegacyUS = (weeks) => {
  if (weeks == null || weeks < 22) return SEVERITY_PALETTE[0];
  if (weeks < 28) return SEVERITY_PALETTE[1];
  if (weeks < 36) return SEVERITY_PALETTE[2];
  if (weeks < 44) return SEVERITY_PALETTE[3];
  if (weeks < 52) return SEVERITY_PALETTE[4];
  return SEVERITY_PALETTE[5];
};

const getTileColor = (tile) => {
  const tiers = tile.surcharge_tiers;
  if (!tiers || !tiers.length)
    return getWeeksColorLegacyUS(tile.utilization_ratio_weeks);
  const index = findTierIndex(tiers, tile.utilization_ratio_weeks);
  if (index < 0) return SEVERITY_PALETTE[0];
  if (tiers.length === 1) return SEVERITY_PALETTE[0];
  const scaled = Math.round(
    (index / (tiers.length - 1)) * (SEVERITY_PALETTE.length - 1)
  );
  return SEVERITY_PALETTE[scaled];
};

const Tile = ({ tile }) => {
  const rate = tile.surcharge_rate ?? 0;
  const unitLabel =
    UNIT_LABEL[tile.surcharge_unit] || tile.surcharge_unit || "";
  const weeksColor = getTileColor(tile);
  const monthlySurcharge = formatCurrency(
    tile.estimated_monthly_surcharge,
    tile.surcharge_currency
  );
  const rateFormatted = formatCurrency(rate, tile.surcharge_currency);
  const hasSurcharge = rate > 0 && monthlySurcharge;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderColor: weeksColor,
        borderWidth: rate > 0 ? 2 : 1,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {tile.store}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {tile.marketplace} · {tile.size_tier}
        </Typography>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 500, lineHeight: 1, color: weeksColor }}
        >
          {formatWeeks(tile.utilization_ratio_weeks)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          weeks of inventory
        </Typography>
        {hasSurcharge && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            ~{monthlySurcharge}/mo at {rateFormatted}/{unitLabel}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const UtilizationRisk = ({ store, marketplace }) => {
  const dispatch = useDispatch();
  const {
    utilizationRisk: data,
    utilizationRiskLoading: loading,
    utilizationRiskErrorMessage: errorMessage,
  } = useSelector(imogenSelector);

  useEffect(() => {
    dispatch(fetchUtilizationRisk({ store, marketplace }));
  }, [dispatch, store, marketplace]);

  const filteredTiles = useMemo(() => {
    if (!data?.tiles) return [];
    return data.tiles;
  }, [data]);

  const sourceFreshness = useMemo(() => {
    if (!data?.data_freshness) return [];
    return Object.entries(data.data_freshness).map(([key, ts]) => ({
      label: SOURCE_LABEL[key] || key,
      timestamp: ts,
    }));
  }, [data]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="error">
        Failed to load utilization risk: {errorMessage}
      </Alert>
    );
  }

  if (!data) return null;

  const lastRefresh = formatLastRefresh(data.computed_at);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        mb={1}
      >
        <Box>
          <Typography variant="h6">Utilization risk</Typography>
          <Typography variant="body2" color="text.secondary">
            Weeks of inventory cover by store and storage tier. Surcharge
            thresholds vary by marketplace and are read directly from Seller
            Central.
          </Typography>
        </Box>
        {lastRefresh && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastRefresh}
          </Typography>
        )}
      </Box>

      {sourceFreshness.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          {sourceFreshness.map((s, i) => (
            <span key={s.label}>
              {i > 0 && " · "}
              {s.label} last updated {formatSourceTimestamp(s.timestamp)}
            </span>
          ))}
        </Typography>
      )}

      {filteredTiles.length === 0 ? (
        <Box p={3}>
          <Typography variant="body2" color="text.secondary">
            No tiles match the current store / marketplace selection.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredTiles.map((tile) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={`${tile.store}-${tile.marketplace}-${tile.size_tier}`}
            >
              <Tile tile={tile} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default UtilizationRisk;
